// Layer: Page (UI) – chỉ render JSX, gọi hook domain từ features.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Table, Row, Col, Typography, Button, Spin, Image } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { ArrowLeft, Download, FileText, Image as ImageIcon } from 'lucide-react';
import type { ColumnsType } from 'antd/es/table';
import { formatRole } from '../utils/formatters';
import type { PersonnelDetail } from '../types/personnelDetail';
import { useEditHistoryDetail } from '@/features/personnel/useEditHistoryDetail';
import styles from './EditHistoryDetailPage.module.css';

const { Text, Title } = Typography;

export const EditHistoryDetailPage: React.FC = () => {
  const navigate = useNavigate();
  
  const {
    data,
    loading,
    avatarUri,
    version,
    handleExport,
    renderValueWithOld,
  } = useEditHistoryDetail();

  // UI handler - navigate back
  const handleBack = () => {
    navigate(-1);
  };

  // Wrapper để thêm className cho phần không có thay đổi
  const renderValue = (fieldName: string, currentValue: any, updateDataField?: string) => {
    const rendered = renderValueWithOld(fieldName, currentValue, updateDataField);
    // Nếu không có thay đổi (rendered là Text đơn giản), thêm className
    if (rendered && React.isValidElement(rendered) && rendered.type === Text) {
      // Kiểm tra xem có phải là Text đơn giản không (không có children phức tạp)
      const children = rendered.props.children;
      if (typeof children === 'string' || (Array.isArray(children) && children.length === 1 && typeof children[0] === 'string')) {
        return <Text className={styles.infoValue}>{currentValue || 'Chưa cập nhật'}</Text>;
      }
    }
    return rendered;
  };

  const currentJobColumns: ColumnsType<PersonnelDetail['currentJob']> = [
    { title: 'CTY', dataIndex: 'company', key: 'company' },
    { title: 'Phòng ban', dataIndex: 'department', key: 'department' },
    { 
      title: 'Chức vụ', 
      dataIndex: 'position', 
      key: 'position',
      render: (text: string) => formatRole(text),
    },
    { title: 'Vị trí', dataIndex: 'role', key: 'role' },
    { title: 'Mức lương', dataIndex: 'salary', key: 'salary' },
    { title: 'Ngày vào làm', dataIndex: 'startDate', key: 'startDate' },
  ];

  type TrainingPlace = NonNullable<PersonnelDetail['trainingPlaces']>[0];
  type WorkExperience = NonNullable<PersonnelDetail['workExperience']>[0];
  type FamilyMember = NonNullable<PersonnelDetail['familyMembers']>[0];
  type Certificate = NonNullable<PersonnelDetail['certificates']>[0];
  type PartyHistory = NonNullable<PersonnelDetail['partyHistory']>[0];

  const trainingColumns: ColumnsType<TrainingPlace> = [
    { title: 'Nơi đào tạo', dataIndex: 'place', key: 'place' },
    { title: 'Chuyên ngành', dataIndex: 'major', key: 'major' },
    { title: 'Hình thức đào tạo', dataIndex: 'type', key: 'type' },
    { title: 'Trình độ', dataIndex: 'degree', key: 'degree' },
    { title: 'Thời gian', dataIndex: 'time', key: 'time' },
  ];

  const experienceColumns: ColumnsType<WorkExperience> = [
    { title: 'Tên CTY', dataIndex: 'company', key: 'company' },
    { title: 'Vị trí', dataIndex: 'role', key: 'role' },
    { title: 'Phòng ban', dataIndex: 'department', key: 'department' },
    { title: 'Mức lương', dataIndex: 'salary', key: 'salary' },
    { title: 'Thời gian', dataIndex: 'time', key: 'time' },
  ];

  const familyColumns: ColumnsType<FamilyMember> = [
    {
      title: 'Phụ thuộc',
      dataIndex: 'relationship',
      key: 'relationship',
      render: (relationship: string) => relationship || '—',
    },
    { title: 'Họ và tên', dataIndex: 'name', key: 'name' },
    {
      title: 'Năm sinh',
      dataIndex: 'birthYear',
      key: 'birthYear',
      render: (birthYear: number | null) => birthYear ?? '—',
    },
    { title: 'Số điện thoại', dataIndex: 'phone', key: 'phone' },
    { title: 'Nghề nghiệp', dataIndex: 'occupation', key: 'occupation' },
    { title: 'Nơi làm việc', dataIndex: 'workplace', key: 'workplace' },
    { title: 'Địa chỉ', dataIndex: 'address', key: 'address' },
  ];

  const certificateColumns: ColumnsType<Certificate> = [
    { title: 'Số hiệu', dataIndex: 'id', key: 'id' },
    { title: 'Đơn vị ban hành', dataIndex: 'issuingBody', key: 'issuingBody' },
    { title: 'Loại chứng chỉ', dataIndex: 'type', key: 'type' },
    { title: 'Thời gian', dataIndex: 'time', key: 'time' },
    { title: 'Ghi chú', dataIndex: 'notes', key: 'notes' },
  ];

  const partyColumns: ColumnsType<PartyHistory> = [
    { title: 'Số thẻ', dataIndex: 'cardNumber', key: 'cardNumber' },
    { title: 'Hình thức', dataIndex: 'form', key: 'form' },
    { title: 'Thời gian', dataIndex: 'time', key: 'time' },
    { title: 'Nơi kết nạp', dataIndex: 'admissionPlace', key: 'admissionPlace' },
    { title: 'Nơi điều chuyển', dataIndex: 'transferPlace', key: 'transferPlace' },
  ];

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.loadingContainer}>
        <Text>Không tìm thấy dữ liệu</Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft} onClick={handleBack}>
          <ArrowLeft size={20} className={styles.backIcon} />
          <Title level={3} className={styles.headerTitle}>
            Lịch sử chỉnh sửa Lần {version}
          </Title>
        </div>
        <Button
          type="default"
          icon={<Download size={16} />}
          onClick={handleExport}
          className={styles.exportButton}
        >
          Xuất file
        </Button>
      </div>

      <div className={styles.contentContainer}>
        <div className={styles.sidebar}>
          <div className={styles.profileCard}>
            <Avatar 
              size={120} 
              icon={<UserOutlined />} 
              src={avatarUri}
              className={styles.avatar} 
            />
            <Title level={4} className={styles.profileName}>
              {data.fullName || 'Đoàn Hữu Trung'}
            </Title>
            <div className={styles.roleTag}>
              {data.currentJob?.role || data.jobTitle || 'UX/UI Designer'}
            </div>
          </div>

          <div className={styles.sidebarSection}>
            <div className={styles.basicInfoList}>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Mã nhân sự:</Text>
                <Text className={styles.basicInfoValue}>{data.employeeCode || 'Chưa cập nhật'}</Text>
              </div>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Email:</Text>
                {renderValue('email', data.email, 'email')}
              </div>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Ngày sinh:</Text>
                {renderValue('dateOfBirth', data.dateOfBirth, 'dateOfBirth')}
              </div>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Giới tính:</Text>
                {renderValue('gender', data.gender, 'gender')}
              </div>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Số điện thoại:</Text>
                {renderValue('phone', data.phone, 'phone')}
              </div>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Ngày gia nhập:</Text>
                <Text className={styles.basicInfoValue}>{data.startDate || 'Chưa cập nhật'}</Text>
              </div>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Phòng ban:</Text>
                <Text className={styles.basicInfoValue}>{data.company || 'Chưa cập nhật'}</Text>
              </div>
            </div>
          </div>

          <div className={styles.sidebarSection}>
            <Title level={5} className={styles.sidebarSectionTitle}>
              Thẻ định danh
            </Title>
            <div className={styles.imageGrid}>
              <div className={styles.imageItem}>
                <Text className={styles.imageLabel}>Ảnh CC/CCCD/CMT Mặt trước</Text>
                {data.idCardFront ? (
                  <Image
                    src={data.idCardFront}
                    alt="ID Card Front"
                    className={styles.idCardImage}
                    preview
                  />
                ) : (
                  <div className={styles.imagePlaceholder}>
                    <ImageIcon size={24} />
                  </div>
                )}
              </div>
              <div className={styles.imageItem}>
                <Text className={styles.imageLabel}>Ảnh CC/CCCD/CMT Mặt sau</Text>
                {data.idCardBack ? (
                  <Image
                    src={data.idCardBack}
                    alt="ID Card Back"
                    className={styles.idCardImage}
                    preview
                  />
                ) : (
                  <div className={styles.imagePlaceholder}>
                    <ImageIcon size={24} />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.sidebarSection}>
            <Title level={5} className={styles.sidebarSectionTitle}>
              Ảnh hộ chiếu
            </Title>
            <div className={styles.imageGrid}>
              <div className={styles.imageItem}>
                {data.passportCover ? (
                  <Image
                    src={data.passportCover}
                    alt="Passport Cover"
                    className={styles.idCardImage}
                    preview
                  />
                ) : (
                  <div className={styles.imagePlaceholder}>
                    <ImageIcon size={24} />
                  </div>
                )}
              </div>
              <div className={styles.imageItem}>
                {data.passportPhoto ? (
                  <Image
                    src={data.passportPhoto}
                    alt="Passport Photo"
                    className={styles.idCardImage}
                    preview
                  />
                ) : (
                  <div className={styles.imagePlaceholder}>
                    <ImageIcon size={24} />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.sidebarSection}>
            <Title level={5} className={styles.sidebarSectionTitle}>
              Lá số tử vi
            </Title>
            <div className={styles.imageItem}>
              {data.horoscope ? (
                <Image
                  src={data.horoscope}
                  alt="Horoscope"
                  className={styles.horoscopeImage}
                  preview
                />
              ) : (
                <div className={styles.largeImagePlaceholder}>
                  <ImageIcon size={32} />
                </div>
              )}
            </div>
          </div>

          <div className={styles.sidebarSection}>
            <Title level={5} className={styles.sidebarSectionTitle}>
              File lưu trữ
            </Title>
            <div className={styles.fileList}>
              {data.storedFiles?.map((file, index) => (
                <div key={index} className={styles.fileItem}>
                  <FileText size={16} className={styles.fileIcon} />
                  <Text className={styles.fileName}>{file.name || 'Thiết kế logo'}</Text>
                  {file.url ? (
                    <a
                      href={file.url}
                      download={file.name || 'download'}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Tải xuống ${file.name || 'file'}`}
                    >
                      <Download size={16} className={styles.downloadIcon} />
                    </a>
                  ) : (
                    <Download size={16} className={styles.downloadIcon} aria-disabled="true" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.mainContent}>
          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Giới thiệu bản thân
            </Title>
            {renderValue('description', data.description, 'selfDescription') || (
              <Text className={styles.infoValue}>{data.description || 'Chưa cập nhật'}</Text>
            )}
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Thông tin chi tiết
            </Title>
            <Row gutter={[24, 16]}>
              <Col span={12}>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Dân tộc:</Text>
                  {renderValue('ethnicity', data.ethnicity, 'ethnicity') || (
                    <Text className={styles.infoValue}>{data.ethnicity || 'Chưa cập nhật'}</Text>
                  )}
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Tôn giáo:</Text>
                  {renderValue('religion', data.religion, 'religion')}
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Nơi sinh:</Text>
                  {renderValue('placeOfBirth', data.placeOfBirth, 'placeOfBirth')}
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Quốc tịch:</Text>
                  {renderValue('nationality', data.nationality, 'nationality')}
                </div>
              </Col>
              <Col span={12}>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Nguyên quán:</Text>
                  {renderValue('hometown', 
                    data.hometown 
                      ? data.hometown.replace('Huyện ', '').replace('Tỉnh ', '').replace(', Việt Nam', '') 
                      : null,
                    'hometown'
                  )}
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Thường trú:</Text>
                  {renderValue('permanentAddress', data.permanentAddress, 'permanentAddress')}
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Địa chỉ:</Text>
                  {renderValue('address', data.address, 'currentAddress')}
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Mã số thuế:</Text>
                  {renderValue('taxCode', data.taxCode, 'personalTaxCode')}
                </div>
              </Col>
            </Row>
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Thông tin ngân hàng
            </Title>
            <Row gutter={[24, 16]}>
              <Col span={12}>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Số Tài Khoản:</Text>
                  {renderValue('bankAccount', data.bankAccount, 'bankAccountNumber')}
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Tên người nhận:</Text>
                  {renderValue('bankAccountHolder', data.bankAccountHolder, 'bankAccountName')}
                </div>
              </Col>
              <Col span={12}>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Ngân hàng:</Text>
                  {renderValue('bankName', data.bankName, 'bankName')}
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Chi nhánh:</Text>
                  {renderValue('bankBranch', data.bankBranch, 'bankBranch')}
                </div>
              </Col>
            </Row>
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Công việc hiện nay
            </Title>
            {data.currentJob && (
              <Table
                columns={currentJobColumns}
                dataSource={[data.currentJob]}
                pagination={false}
                rowKey="company"
                className={styles.table}
              />
            )}
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Học vấn & Kinh nghiệm
            </Title>
            {data.education && (
              <div className={styles.educationInfo}>
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <div className={styles.infoRow}>
                      <Text className={styles.infoLabel}>Trình độ học vấn cao nhất:</Text>
                      <Text className={styles.infoValue}>{data.education.highestLevel}</Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className={styles.infoRow}>
                      <Text className={styles.infoLabel}>Trình độ phổ thông:</Text>
                      <Text className={styles.infoValue}>{data.education.generalLevel}</Text>
                    </div>
                  </Col>
                </Row>
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <div className={styles.infoRow}>
                      <Text className={styles.infoLabel}>Chuyên ngành:</Text>
                      <Text className={styles.infoValue}>{data.education.major}</Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className={styles.infoRow}>
                      <Text className={styles.infoLabel}>Số năm kinh nghiệm trước đây:</Text>
                      <Text className={styles.infoValue}>{data.education.yearsOfExperience}</Text>
                    </div>
                  </Col>
                </Row>
              </div>
            )}

            <div className={styles.subsection}>
              {data.trainingPlaces && (
                <Table
                  columns={trainingColumns}
                  dataSource={data.trainingPlaces}
                  pagination={false}
                  rowKey="place"
                  className={styles.table}
                />
              )}
            </div>

            <div className={styles.subsection}>
              {data.workExperience && (
                <Table
                  columns={experienceColumns}
                  dataSource={data.workExperience}
                  pagination={false}
                  rowKey="company"
                  className={styles.table}
                />
              )}
            </div>
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Thông tin gia đình & người phụ thuộc
            </Title>
            {data.familyMembers && (
              <Table
                columns={familyColumns}
                dataSource={data.familyMembers}
                pagination={false}
                rowKey="name"
                className={styles.table}
              />
            )}
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Chứng chỉ
            </Title>
            {data.certificates && (
              <Table
                columns={certificateColumns}
                dataSource={data.certificates}
                pagination={false}
                rowKey="id"
                className={styles.table}
              />
            )}
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Lịch sử đảng viên
            </Title>
            {data.partyHistory && (
              <Table
                columns={partyColumns}
                dataSource={data.partyHistory}
                pagination={false}
                rowKey="cardNumber"
                className={styles.table}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
